import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  CalendarDaysIcon,
  CurrencyRupeeIcon,
  TagIcon,
  MapPinIcon,
  ArrowsPointingOutIcon,
  PhotoIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  Squares2X2Icon,
  ListBulletIcon
} from '@heroicons/react/24/outline';
import axios from '../../api/axiosConfig';
import { useAuth } from '../../contexts/AuthContext';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Pagination from '../../components/common/Pagination';

interface Structure {
  id: number;
  code: string;
  type: string;
  location: string;
  size?: string;
  currentDailyPrice: number | null;
  currentMonthlyPrice: number | null;
  isActive: boolean;
  imageUrl?: string;
  latitude?: number;
  longitude?: number;
  isLED?: boolean;
}

interface PaginatedStructuresResponse {
  structures: Structure[];
  totalPages: number;
  currentPage: number;
  totalCount: number;
}

// Sorting options
type SortField = 'code' | 'type' | 'location' | 'currentDailyPrice' | 'currentMonthlyPrice' | 'isActive';
type SortDirection = 'asc' | 'desc';
type ViewMode = 'cards' | 'table';

const StructureList: React.FC = () => {
  const { can } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [structures, setStructures] = useState<Structure[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [currentPage, setCurrentPage] = useState(Number(searchParams.get('page')) || 1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [filterType, setFilterType] = useState(searchParams.get('type') || 'all');
  const [sortField, setSortField] = useState<SortField>(searchParams.get('sortField') as SortField || 'code');
  const [sortDirection, setSortDirection] = useState<SortDirection>(searchParams.get('sortDirection') as SortDirection || 'asc');
  const [viewMode, setViewMode] = useState<ViewMode>((searchParams.get('view') as ViewMode) || 'cards');
  const [limit] = useState(12);

  const fetchStructures = useCallback(async (
    page = currentPage, 
    search = searchTerm, 
    type = filterType,
    sort = sortField,
    direction = sortDirection
  ) => {
    setLoading(true);
    setError(null);

    const params = new URLSearchParams();
    const pageNumber = Number.isInteger(Number(page)) && Number(page) > 0 ? Number(page) : 1;
    params.append('page', String(pageNumber));
    params.append('limit', String(limit));
    if (search) params.append('search', search);
    if (type && type !== 'all') params.append('type', type);
    if (sort) params.append('sort', sort);
    if (direction) params.append('direction', direction);

    setSearchParams(params, { replace: true });

    console.log('Fetching structures with params:', params.toString());

    try {
      const response = await axios.get<PaginatedStructuresResponse>('/structures', { params });
      setStructures(response.data.structures);
      setTotalPages(response.data.totalPages);
      setCurrentPage(response.data.currentPage);
      setTotalCount(response.data.totalCount);
    } catch (err) {
      console.error('Error fetching structures:', err);
      setError('Ошибка при загрузке конструкций.');
      setStructures([]);
      setTotalPages(1);
      setCurrentPage(1);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [limit, setSearchParams, currentPage, searchTerm, filterType, sortField, sortDirection]);

  useEffect(() => {
    fetchStructures(currentPage, searchTerm, filterType, sortField, sortDirection);
  }, [fetchStructures, currentPage, searchTerm, filterType, sortField, sortDirection]);

  const handleDelete = async (id: number) => {
    if (!can('structures.delete')) return;
    if (window.confirm('Вы уверены, что хотите удалить эту конструкцию? Это действие нельзя отменить.')) {
      try {
        await axios.delete(`/structures/${id}`);
        fetchStructures(currentPage, searchTerm, filterType, sortField, sortDirection);
      } catch (err: any) {
        console.error('Error deleting structure:', err);
        alert(err.response?.data?.message || 'Ошибка при удалении конструкции');
      }
    }
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };

  const handleTypeFilterChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setFilterType(event.target.value);
    setCurrentPage(1);
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode);
    const params = new URLSearchParams(searchParams);
    params.set('view', mode);
    setSearchParams(params, { replace: true });
  };

  const resetFilters = () => {
    setSearchTerm('');
    setFilterType('all');
    setSortField('code');
    setSortDirection('asc');
    setCurrentPage(1);
  };

  const structureTypes = useMemo(() => {
    return ['all', 'LED Экран', 'Ситиборд', 'Мупи', 'Услуга', 'Другое'];
  }, []);

  const formatCurrency = (amount: number | null | undefined): string => {
    if (amount === null || amount === undefined) return '-';
    return new Intl.NumberFormat('ru-KZ', {
      style: 'currency',
      currency: 'KZT',
      maximumFractionDigits: 2
    }).format(amount);
  };

  const isCityboard = (structureType: string) => structureType === 'Ситиборд';

  const SortableHeader: React.FC<{
    field: SortField;
    children: React.ReactNode;
    className?: string;
  }> = ({ field, children, className = '' }) => (
    <button
      onClick={() => handleSort(field)}
      className={`flex items-center space-x-1 font-medium text-gray-900 hover:text-blue-600 focus:outline-none ${className}`}
    >
      <span>{children}</span>
      {sortField === field && (
        sortDirection === 'asc' ? 
          <ChevronUpIcon className="h-4 w-4" /> : 
          <ChevronDownIcon className="h-4 w-4" />
      )}
    </button>
  );

  const CardView = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
      {structures.map((structure) => (
        <div key={structure.id} className="bg-white rounded-lg shadow overflow-hidden flex flex-col group">
          <Link to={`/structures/${structure.id}`} className="block h-48 bg-gray-200 flex items-center justify-center overflow-hidden">
            {structure.imageUrl ? (
              <img
                src={structure.imageUrl}
                alt={`${structure.code} - ${structure.type}`}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 ease-in-out"
                onError={(e) => (e.currentTarget.src = "/placeholder-image.svg")}
              />
            ) : (
              <PhotoIcon className="h-16 w-16 text-gray-400" />
            )}
          </Link>

          <div className="p-4 flex-grow flex flex-col justify-between">
            <div>
              <div className="flex justify-between items-center mb-1">
                <h3 className="font-bold text-md text-gray-900" title={`${structure.code} - ${structure.type}`}>
                  {structure.code}
                </h3>
                <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${structure.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                  {structure.isActive ? 'Активна' : 'Неактивна'}
                </span>
              </div>
              <p className="text-xs text-gray-500 mb-2 flex items-center">
                <TagIcon className="h-3 w-3 mr-1 flex-shrink-0" />
                {structure.type}
              </p>
              <p className="text-sm text-gray-700 mb-1 flex items-start break-words">
                <MapPinIcon className="h-4 w-4 mr-1 mt-0.5 text-gray-400 flex-shrink-0" />
                {structure.location}
              </p>
              {structure.size && (
                <p className="text-xs text-gray-500 mb-2 flex items-center">
                  <ArrowsPointingOutIcon className="h-3 w-3 mr-1 flex-shrink-0" />
                  Размер: {structure.size}
                </p>
              )}
            </div>

            <div className="mt-3 border-t pt-3">
              {!isCityboard(structure.type) && (
                <div className="flex justify-between items-center mb-1 text-sm">
                  <span className="text-gray-600">День:</span>
                  <span className="font-medium text-gray-800">{formatCurrency(structure.currentDailyPrice)}</span>
                </div>
              )}
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-600">Месяц:</span>
                <span className="font-medium text-gray-800">{formatCurrency(structure.currentMonthlyPrice)}</span>
              </div>
            </div>

            <div className="mt-4 pt-3 border-t flex justify-end items-center space-x-2">
              <Link
                to={`/structures/${structure.id}`}
                className="btn-link text-xs"
                title="Детали"
              >
                Подробнее
              </Link>
              {can('structures.edit') && (
                <Link
                  to={`/structures/edit/${structure.id}`}
                  className="btn-icon text-blue-600 hover:text-blue-800"
                  title="Редактировать"
                >
                  <PencilIcon className="h-4 w-4" />
                </Link>
              )}
              {can('structures.delete') && (
                <button
                  type="button"
                  onClick={() => handleDelete(structure.id)}
                  className="btn-icon text-red-600 hover:text-red-800"
                  title="Удалить"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const TableView = () => (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              <SortableHeader field="code">
                Код
              </SortableHeader>
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              <SortableHeader field="type">
                Тип
              </SortableHeader>
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              <SortableHeader field="location">
                Адрес
              </SortableHeader>
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Размер
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              <SortableHeader field="currentDailyPrice">
                Цена за день
              </SortableHeader>
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              <SortableHeader field="currentMonthlyPrice">
                Цена за месяц
              </SortableHeader>
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              <SortableHeader field="isActive">
                Статус
              </SortableHeader>
            </th>
            <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Действия
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {structures.map((structure) => (
            <tr key={structure.id} className="hover:bg-gray-50">
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center">
                  <div className="flex-shrink-0 h-10 w-10 bg-gray-200 rounded-lg flex items-center justify-center">
                    {structure.imageUrl ? (
                      <img
                        className="h-10 w-10 rounded-lg object-cover"
                        src={structure.imageUrl}
                        alt=""
                      />
                    ) : (
                      <PhotoIcon className="h-6 w-6 text-gray-400" />
                    )}
                  </div>
                  <div className="ml-4">
                    <div className="text-sm font-medium text-gray-900">
                      {structure.code}
                    </div>
                  </div>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900">{structure.type}</div>
              </td>
              <td className="px-6 py-4">
                <div className="text-sm text-gray-900 max-w-xs truncate" title={structure.location}>
                  {structure.location}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-500">{structure.size || '-'}</div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900">
                  {!isCityboard(structure.type) ? formatCurrency(structure.currentDailyPrice) : '-'}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900">
                  {formatCurrency(structure.currentMonthlyPrice)}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                  structure.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {structure.isActive ? 'Активна' : 'Неактивна'}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                <div className="flex justify-end space-x-2">
                  <Link
                    to={`/structures/${structure.id}`}
                    className="text-blue-600 hover:text-blue-900 text-sm"
                  >
                    Подробнее
                  </Link>
                  {can('structures.edit') && (
                    <Link
                      to={`/structures/edit/${structure.id}`}
                      className="text-indigo-600 hover:text-indigo-900 ml-4"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </Link>
                  )}
                  {can('structures.delete') && (
                    <button
                      onClick={() => handleDelete(structure.id)}
                      className="text-red-600 hover:text-red-900 ml-2"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  if (loading && (!structures || structures.length === 0)) {
    return <LoadingSpinner />;
  }

  return (
    <div>
      <PageHeader
        title="Конструкции и услуги"
        subtitle={`Всего найдено: ${totalCount}`}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {can('structures.availability_view') && (
              <Link
                to="/structures/availability"
                className="btn-secondary inline-flex items-center text-sm"
              >
                <CalendarDaysIcon className="h-4 w-4 mr-1" />
                Доступность (Список)
              </Link>
            )}
            {can('structures.availability_view') && (
              <Link
                to="/structures/occupancy-calendar"
                className="btn-secondary inline-flex items-center text-sm"
              >
                <CalendarDaysIcon className="h-4 w-4 mr-1" />
                Календарь Занятости
              </Link>
            )}
            {can('structures.create') && (
              <Link
                to="/structures/new"
                className="btn-primary inline-flex items-center text-sm"
              >
                <PlusIcon className="h-4 w-4 mr-1" />
                Добавить
              </Link>
            )}
          </div>
        }
      />

      {error && (
        <div className="bg-red-100 text-red-700 p-4 rounded-md mb-4">
          {error}
        </div>
      )}

      <div className="mb-6 p-4 bg-gray-50 rounded-lg shadow-sm">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4 items-end">
          <div className="sm:col-span-1 md:col-span-2">
            <label htmlFor="search" className="form-label">Поиск</label>
            <div className="relative">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none" />
              <input
                id="search"
                type="text"
                placeholder="Код, тип, адрес..."
                className="form-input pl-10 w-full"
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </div>
          </div>

          <div className="sm:col-span-1 md:col-span-1">
            <label htmlFor="typeFilter" className="form-label">Тип конструкции</label>
            <select
              id="typeFilter"
              value={filterType}
              onChange={handleTypeFilterChange}
              className="form-input w-full"
            >
              {structureTypes.map((type) => (
                <option key={type} value={type}>
                  {type === 'all' ? 'Все типы' : type}
                </option>
              ))}
            </select>
          </div>

          <div className="sm:col-span-1 md:col-span-1">
            <label htmlFor="sortField" className="form-label">Сортировка</label>
            <select
              id="sortField"
              value={`${sortField}-${sortDirection}`}
              onChange={(e) => {
                const [field, direction] = e.target.value.split('-');
                setSortField(field as SortField);
                setSortDirection(direction as SortDirection);
                setCurrentPage(1);
              }}
              className="form-input w-full"
            >
              <option value="code-asc">Код (А-Я)</option>
              <option value="code-desc">Код (Я-А)</option>
              <option value="type-asc">Тип (А-Я)</option>
              <option value="type-desc">Тип (Я-А)</option>
              <option value="location-asc">Адрес (А-Я)</option>
              <option value="location-desc">Адрес (Я-А)</option>
              <option value="currentMonthlyPrice-asc">Цена за месяц (по возрастанию)</option>
              <option value="currentMonthlyPrice-desc">Цена за месяц (по убыванию)</option>
              <option value="currentDailyPrice-asc">Цена за день (по возрастанию)</option>
              <option value="currentDailyPrice-desc">Цена за день (по убыванию)</option>
              <option value="isActive-asc">Статус (Активные сначала)</option>
              <option value="isActive-desc">Статус (Неактивные сначала)</option>
            </select>
          </div>

          <div className="sm:col-span-1 md:col-span-1 flex items-end space-x-2">
            <div className="flex-1">
              <label className="form-label block mb-1">Вид</label>
              <div className="flex border border-gray-300 rounded-md overflow-hidden">
                <button
                  onClick={() => handleViewModeChange('cards')}
                  className={`flex-1 flex items-center justify-center px-3 py-2 text-sm transition-colors ${
                    viewMode === 'cards'
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                  title="Плитки"
                >
                  <Squares2X2Icon className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleViewModeChange('table')}
                  className={`flex-1 flex items-center justify-center px-3 py-2 text-sm transition-colors ${
                    viewMode === 'table'
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                  title="Таблица"
                >
                  <ListBulletIcon className="h-4 w-4" />
                </button>
              </div>
            </div>

            <button
              type="button"
              onClick={resetFilters}
              className="btn-secondary-outline inline-flex items-center text-sm whitespace-nowrap"
              disabled={loading}
            >
              <XMarkIcon className="h-4 w-4 mr-1" />
              Сбросить
            </button>
          </div>
        </div>
      </div>

      {loading && <LoadingSpinner />}

      {!loading && !error && (!structures || structures.length === 0) && (
        <div className="text-center py-12 text-gray-500">
          Конструкции не найдены.
        </div>
      )}

      {!loading && structures && structures.length > 0 && (
        <>
          {viewMode === 'cards' ? <CardView /> : <TableView />}

          {totalPages > 1 && (
            <div className="mt-6">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default StructureList;
